<template>
	<div>
		<div class='knd-map'>		
			<div 
				class="knd-map__map" 
				ref="map"></div>

			<menu>
				<button
					class="knd-map__controls minus"
					v-show="showButtons"
					v-on:click="zoom(-1)">
					<img :src="require('@/assets/icons/icon-minus.svg')" />
				</button>

				<button
					class="knd-map__controls plus"
					v-show="showButtons"
					v-on:click="zoom(1)">
					<img :src="require('@/assets/icons/icon-plus.svg')" />
				</button>
			</menu>

			<div class="knd-map__bron">
				<p class="subtext">Bron: <a href="https://locatus.com/" target="_blank">Locatus</a></p>
			</div>

			<div class="knd-map__jaartal"><p>{{ jaar }}</p></div>
		</div>
	</div>
</template>

<script>
	import 'ol/ol.css';
	import Map from 'ol/Map';
	import View from 'ol/View';
	import TileLayer from 'ol/layer/Tile';
	import OSM from 'ol/source/OSM';
	import VectorLayer from 'ol/layer/Vector';
	import VectorSource from 'ol/source/Vector';
	import XYZ from 'ol/source/XYZ';
	import {fromLonLat} from 'ol/proj';
	import * as interaction from 'ol/interaction';
	import {defaults} from 'ol/interaction';
	import olms from 'ol-mapbox-style';
	import Style from 'ol/style/Style';
	import Feature from 'ol/Feature';
	import Point from 'ol/geom/Point';
	import Icon from 'ol/style/Icon';
	import Polygon from 'ol/geom/Polygon';
	import MultiPolygon from 'ol/geom/MultiPolygon';
	import Fill from 'ol/style/Fill';
	import Stroke from 'ol/style/Stroke';
	import {getVectorContext} from 'ol/render';
	import Observable from 'ol/Observable';
	import TopoJSON from 'ol/format/TopoJSON';


	export default {
		name: 'Map',
		props: ['userLoc', 'step'],
		data() {
			return {
				"map": null,
				"view": null,
				"key": 'pk.eyJ1Ijoid2V5b25jZSIsImEiOiJja3V4c3JrZHgyOXJuMm5sbnBmeDdrZG1pIn0.xEdkptASss8kKDk1DjgxUw',
				"style_url": 'https://api.mapbox.com/styles/v1/weyonce/ckuxtguqy1qhc18my6ekpdcs6',
				"dotLayers": {
					"layer_2011": null,
					"layer_2021": null
				},
				"buurtLayer": null,
				"gemeenteLayer": null,
				"gemeenteExtent": null,
				"jaar": 2011
			}
		},
		computed: {
			showButtons() {
				return true;
			},

			buurtSelected() {
				return this.$store.getters.buurtSelected;
			},

			gemeenteSelected() {
				return this.$store.getters.gemeenteSelected;
			},

			gemeenteAlle() {
				return this.$store.getters.gemeenteAlle;
			},

			userLocation() {
				return this.$store.getters.userLocation;
			},

			buurtScenario() {
				return this.$store.getters.buurtScenario;
			}
		},
		mounted() {
			this.initMap();
		},
		watch: {
			step: function (newVal, oldVal) { // watch it
				switch(newVal) {
					case 2:
						if (this.buurtScenario === "daling" || this.buurtScenario === "geen") {
							this.mapToLoc(this.gemeenteExtent);
							this.highlightGemeente(this.gemeenteSelected.gemeente_code);
							this.highlightBuurt("");
						}
						else {
							if (this.buurtSelected) {
								this.mapToBuurt(this.buurtSelected);
								this.highlightBuurt(this.buurtSelected.buurt_code);
								this.highlightGemeente("");
							}
						}
						break;
					case 3: 
						this.jaar = 2011;
						this.map.removeLayer(this.dotLayers.layer_2021);
						break;	
					case 4:
						this.jaar = 2021;
						this.drawDots(2021);
				}
			},

			userLocation(newValue) {
				this.findGemeente(newValue);
			},

			gemeenteSelected: { //gemeente gevonden
				handler() { 
					this.drawBuurten();
					this.findBuurt(); //is gebruiker in deze gemeente?
					this.drawDots(2011);
				}, 
				deep: true
			},

			buurtSelected: {
				handler() {
					this.mapToBuurt(this.buurtSelected);
					this.highlightBuurt(this.buurtSelected.buurt_code);
					this.highlightGemeente("");
				},
				deep: true
			}
		},
		methods: {
			initMap() {
				this.view = new View({
					center: fromLonLat(this.userLocation),
					zoom: 17
				});

				this.map = new Map({
					controls: [],
					target: this.$refs.map,
					view: this.view,
					interactions: interaction.defaults({mouseWheelZoom:false, dragPan: false}),
				});

				olms(this.map, this.style_url + '?access_token=' + this.key);

				this.drawGemeenten();
				this.findGemeente(this.userLocation);
			},

			drawGemeenten() {
				let vectorSource = new VectorSource({
					features: (new TopoJSON({layers:'gemeenten2019_zz'})).readFeatures(this.$store.state.gemeenteAlle, {featureProjection: 'EPSG:3857', dataProjection:'EPSG:4326'})
				});

				this.gemeenteLayer = new VectorLayer({
					source: vectorSource,
					style: (feature) => {
						return this.getStyle(feature);
					}
				});

				this.map.addLayer(this.gemeenteLayer);
			},

			getStyle(feature) {
				let style = new Style({
					fill: new Fill({
						color: 'rgba(255, 255, 255, .5)',
					})
				});

				return style;
			},

			drawBuurten() {
				this.map.removeLayer(this.buurtLayer);

				let features = [];

				this.gemeenteSelected.buurten.forEach((buurt, buurtindex) => {
					let coords = [[]];

					buurt.buurt_polygon[0].forEach((coord) => {
						coords[0].push(fromLonLat(coord));
					});

					const feature = new Feature({
						geometry : new Polygon(coords)
					});

					feature.set('buurt_code', buurt.buurt_code);
					feature.set('buurt_naam', buurt.buurt_naam);

					features.push(feature);
				});

				let featureLayer = new VectorSource({
					features : features
				});

				this.gemeenteExtent = featureLayer.getExtent();

				this.buurtLayer = new VectorLayer({
					className: 'buurt-layer',
					source : featureLayer
				});

				this.map.addLayer(this.buurtLayer);
			},

			zoom(delta) {
				const zoom = this.view.getZoom();

				this.view.animate({
					duration: 300,
					zoom: zoom + delta
				});
			},

			mapToLoc(extent) {
				this.view.fit(extent, { 
					padding: [50, 50, 50, 50],
					duration: 1000 
				});
			},

			findGemeente(location) {
				if(!this.gemeenteLayer) return;
				const features = this.gemeenteLayer.getProperties().source.getFeatures();

				features.forEach((feature) => {
					var featureGeometry = feature.getGeometry();
					if (featureGeometry.intersectsCoordinate(fromLonLat(location))) {
						let code = feature.get('GM_CODE');
						this.$store.dispatch('loadGemeente', {code});
					}
				});
			},

			findBuurt() {
				if(!this.userLocation) return;
				const features = this.buurtLayer.getProperties().source.getFeatures();

				features.forEach((feature) => {
					var featureGeometry = feature.getGeometry();
					if (featureGeometry.intersectsCoordinate(fromLonLat(this.userLocation))) {
						const buurt = this.gemeenteSelected.buurten.find((d) => {
							return d.buurt_code === feature.values_.buurt_code;
						});
						this.$store.dispatch('updateBuurt', buurt);
					}
				});
			},

			mapToBuurt(buurt) {
				const features = this.buurtLayer.getProperties().source.getFeatures();

				features.forEach((feature) => {
					if (feature.values_.buurt_code === buurt.buurt_code) {
						var featureGeometry = feature.getGeometry();
						var extent = featureGeometry.getExtent();
						this.mapToLoc(extent);
					}
				});
			},

			highlightBuurt(buurt_code) {
				const features = this.buurtLayer.getProperties().source.getFeatures();

				features.forEach((feature) => {
					if (feature.values_.buurt_code == buurt_code) {
						feature.setStyle(new Style({
							fill: new Fill({
								color: 'rgba(252, 244, 11, .8)',
							})
						}));
					}
					else {
						feature.setStyle(new Style({
							fill: new Fill({
								color: 'rgba(255, 255, 255, 0)',
							})
						}));
					}
				});
			},

			highlightGemeente(gemeente_code) {
				const features = this.gemeenteLayer.getProperties().source.getFeatures();

				features.forEach((feature) => {
					if (feature.values_.GM_CODE == gemeente_code) {
						feature.setStyle(new Style({
							fill: new Fill({
								color: 'rgba(252, 244, 11, .8)',
							})
						}));
					}
					else {
						feature.setStyle(new Style({
							fill: new Fill({
								color: 'rgba(255, 255, 255, 0)',
							})
						}));
					}
				});
			},

			drawDots(jaar) {
				this.map.removeLayer(this.dotLayers["layer_" + jaar]);

				let features = this.makeDots(jaar);

				let featureLayer = new VectorSource({
					features : features
				});

				this.dotLayers["layer_" + jaar] = new VectorLayer({
					className: 'dot-layer ' + jaar,
					source : featureLayer
				});

				this.map.addLayer(this.dotLayers["layer_" + jaar]);
			},

			makeDots(jaar) {
				let features = []

				this.gemeenteSelected.buurten.forEach((buurt, buurtindex) => {
					buurt.locaties.forEach((locatie, locatieindex) => {
						if (locatie.jaar === jaar) {
							const lonlat = fromLonLat([locatie.lon, locatie.lat])
							const svg = this.getMarkerCode(locatie.jaar);

							const feature = new Feature({
								geometry : new Point(lonlat)
							});

							feature.setStyle(new Style({
								image : new Icon({
									src : svg
								})
							}));

							features.push(feature);
						}
					});
				});

				return features;
			},

			getMarkerCode(jaar) {
				const stroke = "#07034A";
				// const color = jaar === 2011 ? '#FF6D61' : '#FCF40B';
				const color = jaar === 2011 ? '#bbb' : '#FF6D61';
				const opacity = jaar === 2011 ? '1' : '1';
				const size = 50;
				const r = 20;

				const svg = `
				<svg version="1.0" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="` + r + `px"
					 height="` + r + `px" viewBox="0 0 ` + (r * 2) + ` ` + (r * 2) + `">
					<circle fill="${color}" stroke="${stroke}" stroke-width="3px" opacity="` + opacity + `" cx="` + r + `" cy="` + r + `" r="` + (r - 3) + `" />
				</svg>
				`;

				const base64 = window.btoa(svg);
				return `data:image/svg+xml;base64,${base64}`;
			}
		}
	}
</script>